/** Declared card actions use the same forms and command receipts as plugin pages. */
import { useRef, useState } from "react";
import { api, ApiError, type ChatMessage, type CommandStatus } from "../lib/api";
import { useDraft } from "../lib/drafts";
import { ActionButton, type RunAction } from "./blocks";
import { Accordion, Button } from "../components/rumonai";
import { FormBlockView } from "./form";
import { FormDraftScope } from "./InteractionFrame";
import { SurfaceAction } from "./SurfaceAction";
import type { DrawnRecord } from "./records";
import type { PanelAction, PanelSpec } from "./spec";

interface Submission { id: string; action: string; values: Record<string, unknown>; obligation?: string }

export function RecordControls({ message, card, onToast }: {
  message: ChatMessage; card: DrawnRecord;
  onToast: (text: string, tone: "good" | "critical") => void;
}) {
  const spec = card.panel.spec as PanelSpec;
  const scope = `record:${message.id}:${card.panel.key}`;
  const [pending, setPending, draftError] = useDraft<Submission | null>(`${scope}:press`, null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<Record<number, string[]>>({});
  const sending = useRef(false);
  const [busy, setBusy] = useState(false);
  const all = [...(spec.actions ?? []), ...spec.blocks.flatMap(block => block.actions ?? [])];
  const offered = (action: PanelAction) => !action.record_states?.length || action.record_states.includes(card.read.state);
  const waitingAction = all.find(action => action.key === pending?.action);
  const run: RunAction = async (action, values) => {
    if (sending.current) return pending?.obligation ?? "";
    if (pending && (pending.action !== action.key || JSON.stringify(pending.values) !== JSON.stringify(values)))
      throw new Error("上一份操作还在核对，请先查看它的结果。");
    if (pending?.obligation) return pending.obligation;
    // Secrets have the host's dedicated input path, never a retained card draft.
    if (spec.blocks.some(block => block.fields?.some(field => field.secret && values[field.key])))
      throw new Error("密钥请通过安全输入填写。");
    const request = pending ?? { id: `record-${crypto.randomUUID()}`, action: action.key, values };
    sending.current = true; setBusy(true); setError(""); setPending(request);
    try {
      const answer = await api.panelAction(card.panel.key, { key: action.key }, values, {
        command_id: request.id, record_call_id: message.call_id!, session_id: message.session_id,
      });
      if (!answer.obligation_id) throw new Error("尚未收到操作回执，请核对这次提交。");
      setPending({ ...request, obligation: answer.obligation_id });
      return answer.obligation_id;
    } catch (failure) {
      const reason = ApiError.fromUnknown(failure);
      setError(reason.detail);
      if (reason.status >= 400 && reason.status < 500) setPending(null);
      throw failure;
    } finally { sending.current = false; setBusy(false); }
  };
  const settled = (status: CommandStatus) => {
    if (status.known && ["completed", "failed", "stopped"].includes(status.action_state ?? "")) {
      setPending(null);
      if (status.action_state === "completed") onToast("操作已完成", "good");
      else setError(status.error || "这次操作未完成，填写的内容仍保留。");
    }
  };
  const actions = (spec.actions ?? []).filter(offered);
  const forms = spec.blocks.map((block, index) => ({ block, index }))
    .filter(({ block }) => block.kind === "form" && block.actions?.some(offered));
  if (!actions.length && !forms.length && !pending) return null;
  return <div className="chat-record-controls">
    <FormDraftScope.Provider value={scope}>
      {forms.map(({ block, index }) => <div className="chat-record-fold" key={index} data-open={Boolean(expanded[index]?.length) || undefined}>
        <Accordion value={expanded[index] ?? []} onValueChange={value => setExpanded(current => ({ ...current, [index]: value }))}
          items={[{ value: "form", title: block.title, content: <fieldset disabled={busy || Boolean(pending)}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <FormBlockView block={{ ...block, title: "", actions: block.actions?.filter(offered) }} index={index}
              idPrefix={scope + ":" + index} reads={[]} busy={busy ? "press" : null} runAction={run} />
          </fieldset> }]} />
      </div>)}
      <fieldset disabled={busy || Boolean(pending)} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <div className="form-actions">{actions.map(action => <ActionButton key={action.key} action={action}
          values={{}} id={action.key} busy={busy ? action.key : null} runAction={run} />)}</div>
      </fieldset>
    </FormDraftScope.Provider>
    {draftError || error ? <p role="alert" className="micro">{draftError || error}</p> : null}
    {pending?.obligation ? <SurfaceAction submission={{ id: pending.obligation, label: waitingAction?.label || "这次操作" }}
      onStatus={settled} onToast={onToast} /> : pending ? <Button variant="secondary" data-action="check" disabled={busy || !waitingAction}
      onClick={() => { if (waitingAction) void run(waitingAction, pending.values, "retry").catch(() => {}); }}>核对这次提交</Button> : null}
  </div>;
}
